"use client";

import type * as React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { CreditCard as CreditCardIcon } from "@phosphor-icons/react/dist/ssr/CreditCard";
import { LockKey as LockKeyIcon } from "@phosphor-icons/react/dist/ssr/LockKey";
import { User as UserIcon } from "@phosphor-icons/react/dist/ssr/User";

import { appConfig } from "@/config/app";
import { paths } from "@/paths";
import { AuthStrategy } from "@/lib/auth-strategy";
import { RouterLink } from "@/components/core/link";

import { Auth0SignOut } from "./auth0-sign-out";
import { ClerkSignOut } from "./clerk-sign-out";
import { CognitoSignOut } from "./cognito-sign-out";
import { CustomSignOut } from "./custom-sign-out";
import { SupabaseSignOut } from "./supabase-sign-out";
import { User } from "../../../../types/user";
import { t } from "i18next";
import { hasFeature } from "../../../feature/hasFeature";
import { getStoredAccessToken } from "../../../auth/tokenManager";
import { Button } from "@mui/material";
import { useState } from "react";
import { toast } from "../../../core/toaster";

function SignOutButton(): React.JSX.Element {
    if (appConfig.authStrategy === AuthStrategy.AUTH0) {
        return <Auth0SignOut />;
    }

    if (appConfig.authStrategy === AuthStrategy.CLERK) {
        return <ClerkSignOut />;
    }

    if (appConfig.authStrategy === AuthStrategy.COGNITO) {
        return <CognitoSignOut />;
    }

    if (appConfig.authStrategy === AuthStrategy.CUSTOM) {
        return <CustomSignOut />;
    }

    if (appConfig.authStrategy === AuthStrategy.SUPABASE) {
        return <SupabaseSignOut />;
    }

    return (
        <MenuItem component="a" href={paths.home} sx={{ justifyContent: "center" }}>
            {t("common:signOut")}
        </MenuItem>
    );
}

export interface UserPopoverProps {
    anchorEl: null | Element;
    onClose?: () => void;
    open: boolean;
    user: User;
}

export function UserPopover({ anchorEl, onClose, open, user }: UserPopoverProps): React.JSX.Element {

    const mode = import.meta.env.MODE;

    const visibleAccount = hasFeature(user, "user:account:show");
    const visibleNotifications = hasFeature(user, "user:notifications:show");
    const showAuth0Token = mode === "localdev";

    const [copied, setCopied] = useState(false);

    async function copyToClipboard(token: string | null) {

        if (!showAuth0Token) {
            return;
        }

        if (token === null) {
            return;
        }

        try {
            await navigator.clipboard.writeText(token);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            setCopied(false);
        }
    }

    return (
        <Popover
            anchorEl={anchorEl}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            onClose={onClose}
            open={Boolean(open)}
            slotProps={{ paper: { sx: { width: "280px" } } }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
        >
            <Box sx={{ p: 2 }}>
                <Typography fontWeight="bold">
                    {user.name} <Typography component="span" fontSize={11}>({user.username})</Typography>
                </Typography>
                <Typography color="text.secondary" variant="body2">
                    {user.email}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                    {user.level}
                </Typography>
            </Box>
            <Divider />
            <List sx={{ p: 1 }}>
                {
                    visibleAccount === false
                        ? <></>
                        : <MenuItem component={RouterLink} href={paths.dashboard.user.account} onClick={onClose}>
                            <ListItemIcon>
                                <UserIcon />
                            </ListItemIcon>
                            {t("navbar:user:account")}
                        </MenuItem>
                }
                {
                    visibleNotifications === false
                        ? <></>
                        : <MenuItem component={RouterLink} href={paths.dashboard.user.notifications} onClick={onClose}>
                            <ListItemIcon>
                                <LockKeyIcon />
                            </ListItemIcon>
                            {t("navbar:user:notifications")}
                        </MenuItem>
                }
            </List>
            <Divider />
            {
                showAuth0Token && (
                    <>
                        <Box sx={{ p: 2 }}>
                            <Typography color="text.secondary" variant="body2">
                                {getStoredAccessToken()}
                            </Typography>
                        </Box>

                        <Button
                            sx={{ ml: 0, width: "100%" }}
                            variant="contained"
                            onClick={() => copyToClipboard(getStoredAccessToken())}>
                            Token kopieren
                        </Button>
                        <Divider />
                    </>
                )
            }
            <Box sx={{ p: 1 }}>
                <SignOutButton />
            </Box>
        </Popover>
    );
}
